import { db } from '@/firebase/init'
import { useUserStore } from '@/stores/userStore'
import type { ITextList } from '@team-milva/lab-models/resources'
import { collection, onSnapshot, query, where } from 'firebase/firestore'
import { defineStore } from 'pinia'

// Read-only lab catalog data (Apps/-lab-management-/TextLists) — MIL-338
// Phase 2. Covers the two system lists (result-texts, result-units, keyed by
// systemKey). The lab-authored custom lists were removed from the platform
// (superseded by ReportDescription) — no class exists for this resource in
// @team-milva/lab-models (types only), so state holds the plain ITextList
// shape directly.
export const useTextListStore = defineStore('textList', {
  state: () => ({
    allTextLists: [] as ITextList[],
    unsubscribeAllTextLists: null as (() => void) | null,
  }),

  actions: {
    getTextLists(): Promise<ITextList[]> {
      if (this.allTextListsLoaded) {
        return Promise.resolve(this.allTextLists)
      }

      const userStore = useUserStore()

      return new Promise((resolve) => {
        if (!userStore.currentUser) {
          return resolve([])
        }

        let resolved = false
        const q = query(
          collection(db, 'Apps', '-lab-management-', 'TextLists'),
          where('labAccountID', '==', userStore.currentUser.labAccountID)
        )

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          const textLists: ITextList[] = []
          querySnapshot.forEach((docSnap) => {
            textLists.push(docSnap.data() as ITextList)
          })
          this.allTextLists = textLists
          this.unsubscribeAllTextLists = unsubscribe

          if (!resolved) {
            resolved = true
            resolve(textLists)
          }
        })
      })
    },

    resetStore(): void {
      this.allTextLists = []
      this.unsubscribeAllTextLists?.()
      this.unsubscribeAllTextLists = null
    },
  },

  getters: {
    allTextListsLoaded: (state) => !!state.unsubscribeAllTextLists,

    getSystemTextList:
      (state) =>
      (systemKey: 'result-texts' | 'result-units') =>
        state.allTextLists.find((x) => x.systemKey === systemKey),
  },
})
